// TaskCard.jsx
import React from 'react'
import { Card, Tag, Avatar, Tooltip, Progress, Badge, Button, Dropdown, Menu, Space, Typography } from 'antd'
import {
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  FileImageOutlined,
  MessageOutlined,
  PaperClipOutlined
} from '@ant-design/icons'
import { useAuth } from '../../contexts/AuthContext'
import dayjs from 'dayjs'
import 'dayjs/locale/th'
import relativeTime from 'dayjs/plugin/relativeTime'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'

// เพิ่ม plugins ให้ dayjs
dayjs.extend(relativeTime)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.locale('th')

const { Text, Paragraph } = Typography

const TaskCard = ({
  task,
  onEdit,
  onDelete,
  onViewComments,
  onDragStart,
  isDragging = false
}) => {
  const { user } = useAuth()

  // ตรวจสอบสถานะความสำคัญ
  const getPriorityConfig = (priority) => {
    const configs = {
      low: { color: 'default', label: 'ต่ำ' },
      medium: { color: 'blue', label: 'ปานกลาง' },
      high: { color: 'orange', label: 'สูง' },
      urgent: { color: 'red', label: 'เร่งด่วน' }
    }
    return configs[priority] || configs.medium
  }

  // ตรวจสอบว่างานเกินกำหนดหรือไม่
  const isOverdue =
    task?.is_overdue ||
    (task?.due_date &&
      dayjs(task?.due_date).isBefore(dayjs(), 'day') &&
      task?.progress_percentage < 100)

  const priorityConfig = getPriorityConfig(task?.priority)

  // สร้างเมนู dropdown
  const actionMenu = (
    <Menu>
      {user?.permissions?.project_tasks?.update && (
        <Menu.Item
          key="edit"
          icon={<EditOutlined />}
          onClick={() => onEdit(task)}
        >
          แก้ไขงาน
        </Menu.Item>
      )}
      <Menu.Item
        key="comments"
        icon={<MessageOutlined />}
        onClick={() => onViewComments(task)}
      >
        ดูความเห็น ({task?.comment_count || 0})
      </Menu.Item>
      {user?.permissions?.project_tasks?.delete && (
        <Menu.Item
          key="delete"
          icon={<DeleteOutlined />}
          danger
          onClick={() => onDelete(task)}
        >
          ลบงาน
        </Menu.Item>
      )}
    </Menu>
  )

  return (
    <Card
      draggable
      onDragStart={(e) => onDragStart?.(e, task)}
      size="small"
      className={`task-card ${isDragging ? 'dragging' : ''}`}
      style={{
        marginBottom: 8,
        cursor: 'grab',
        border: isOverdue ? '1px solid #ff4d4f' : '1px solid #f0f0f0',
        boxShadow: isDragging
          ? '0 4px 12px rgba(0,0,0,0.15)'
          : '0 1px 2px rgba(0,0,0,0.03)'
      }}
      bodyStyle={{ padding: 12 }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 8
        }}
      >
        <div style={{ flex: 1, marginRight: 8 }}>
          <Text strong style={{ fontSize: 14, lineHeight: 1.2 }}>
            {task?.task_title}
          </Text>
          {task?.is_overdue > 0 && (
            <Tooltip title="เกินกำหนด">
              <ExclamationCircleOutlined
                style={{ color: '#ff4d4f', marginLeft: 4, fontSize: 12 }}
              />
            </Tooltip>
          )}
          {task?.progress_percentage === 100 && (
            <Tooltip title="เสร็จสิ้น">
              <CheckCircleOutlined
                style={{ color: '#52c41a', marginLeft: 4, fontSize: 12 }}
              />
            </Tooltip>
          )}
        </div>
        <Dropdown overlay={actionMenu} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            size="small"
            icon={<MoreOutlined />}
            style={{ padding: 0, width: 24, height: 24 }}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      </div>

      {/* Description */}
      {task?.task_description && (
        <Paragraph
          ellipsis={{ rows: 2, expandable: true, symbol: 'เพิ่มเติม' }}
          style={{ fontSize: 12, color: '#666', marginBottom: 8, marginTop: 0 }}
        >
          {task?.task_description}
        </Paragraph>
      )}

      {/* Tags */}
      {task?.tags && (
        <div style={{ marginBottom: 8 }}>
          {task?.tags
            .split(',')
            .slice(0, 3)
            .map((tag, index) => (
              <Tag key={index} size="small" style={{ fontSize: 10, marginBottom: 2 }}>
                {tag.trim()}
              </Tag>
            ))}
          {task?.tags.split(',').length > 3 && (
            <Tag size="small" style={{ fontSize: 10, marginBottom: 2 }}>
              +{task?.tags.split(',').length - 3}
            </Tag>
          )}
        </div>
      )}

      {/* Progress */}
      {task?.progress_percentage > 0 && (
        <div style={{ marginBottom: 8 }}>
          <Progress
            percent={task.progress_percentage}
            size="small"
            strokeColor={isOverdue ? '#ff4d4f' : '#1890ff'}
            format={(percent) => `${percent}%`}
            style={{ marginBottom: 0 }}
          />
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {task?.assigned_to_name ? (
            <Tooltip title={task?.assigned_to_name}>
              <Avatar
                src={task?.assigned_to_avatar}
                size={20}
                icon={<UserOutlined />}
                style={{ fontSize: 10 }}
              />
            </Tooltip>
          ) : (
            <Avatar size={20} icon={<UserOutlined />} style={{ fontSize: 10 }} />
          )}
        </div>

        {task.due_date && (
          <Tooltip title={`กำหนดส่ง: ${dayjs(task?.due_date).format('DD/MM/YYYY')}`}>
            <Text
              type={isOverdue ? 'danger' : 'secondary'}
              style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 2 }}
            >
              <ClockCircleOutlined />
              {dayjs(task.due_date).format('DD/MM')}
              {isOverdue &&
                ` (${Math.abs(dayjs(task.due_date).diff(dayjs(), 'day'))} วัน)`}
            </Text>
          </Tooltip>
        )}
      </div>

      {/* Stats */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 8,
          paddingTop: 8,
          borderTop: '1px solid #f0f0f0'
        }}
      >
        <Space size="small">
          <Badge count={task?.comment_count || 0} size="small">
            <Button
              type="text"
              size="small"
              icon={<MessageOutlined />}
              style={{ padding: 0, height: 'auto', fontSize: 12, color: '#666' }}
              onClick={(e) => {
                e.stopPropagation()
                onViewComments?.(task)
              }}
            />
          </Badge>

          {task.attachment_count > 0 && (
            <Badge count={task.attachment_count} size="small">
              <Button
                type="text"
                size="small"
                icon={<PaperClipOutlined />}
                style={{ padding: 0, height: 'auto', fontSize: 12, color: '#666' }}
              />
            </Badge>
          )}

          {task.image_count > 0 && (
            <Badge count={task.image_count} size="small">
              <Button
                type="text"
                size="small"
                icon={<FileImageOutlined />}
                style={{ padding: 0, height: 'auto', fontSize: 12, color: '#666' }}
              />
            </Badge>
          )}
        </Space>

        <Tag color={priorityConfig.color} size="small" style={{ fontSize: 10 }}>
          {priorityConfig.label}
        </Tag>
      </div>
    </Card>
  )
}

export default TaskCard
